!function () {
	"use strict";

	const TEMPLATE_SELECTOR = "[data-video-template]";
	const LOADING_CLASS = "is-loading";
	const VIDEO_ID_ATTR = "data-video-id";
	const CONTROLS_ATTR = "data-controls";

	const PLAY_ICON = '<svg width="64px" height="64px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <path fill-rule="evenodd" clip-rule="evenodd" d="M5.46484 3.92349C4.79896 3.5739 4 4.05683 4 4.80888V19.1911C4 19.9432 4.79896 20.4261 5.46483 20.0765L19.1622 12.8854C19.8758 12.5108 19.8758 11.4892 19.1622 11.1146L5.46484 3.92349ZM2 4.80888C2 2.55271 4.3969 1.10395 6.39451 2.15269L20.0919 9.34382C22.2326 10.4677 22.2325 13.5324 20.0919 14.6562L6.3945 21.8473C4.39689 22.8961 2 21.4473 2 19.1911V4.80888Z" fill="currentColor"></path> </g></svg>';
	const PAUSE_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d = "M11.332 2.66699H9.9987C9.63051 2.66699 9.33203 2.96547 9.33203 3.33366V12.667C9.33203 13.0352 9.63051 13.3337 9.9987 13.3337H11.332C11.7002 13.3337 11.9987 13.0352 11.9987 12.667V3.33366C11.9987 2.96547 11.7002 2.66699 11.332 2.66699Z" stroke="currentColor" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/><path d="M6 2.66699H4.66667C4.29848 2.66699 4 2.96547 4 3.33366V12.667C4 13.0352 4.29848 13.3337 4.66667 13.3337H6C6.36819 13.3337 6.66667 13.0352 6.66667 12.667V3.33366C6.66667 2.96547 6.36819 2.66699 6 2.66699Z" stroke="currentColor" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/></svg>';

	const MUTED_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H2v6h4l5 4V5z"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>';
	const UNMUTED_ICON = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H2v6h4l5 4V5z"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>';

	if (!customElements.get("video-background")) {
		customElements.define(
			"video-background",
			class extends HTMLElement {
				constructor() {
					super();
					this.powerSaverVideoPlay = this.powerSaverVideoPlay.bind(this);
					this.handleToggleClick = this.handleToggleClick.bind(this);
					this.syncControlsState = this.syncControlsState.bind(this);
					this.handleMuteClick = this.handleMuteClick.bind(this);
					this.syncMuteState = this.syncMuteState.bind(this);
				}

				connectedCallback() {
					// Element can be re-attached (moved in DOM by sliders/section re-renders).
					// Render only once; on re-connect just restore listeners/observers.
					if (this.isRendered) {
						this.observeVideoPlayToggle();
						if (this.hasControls) this.bindControls();
						return;
					}

					this.videoId = this.getAttribute(VIDEO_ID_ATTR);
					this.videoTemplate = this.querySelector(TEMPLATE_SELECTOR);
					this.video = null;
					this.hasControls = this.hasAttribute(CONTROLS_ATTR);

					if (this.videoId) this.renderVideo();
				}

				powerSaverVideoPlay() {
					this.video?.play();
				}

				renderVideo() {
					this.videoTemplateObserver?.disconnect();
					this.videoTemplateObserver = new IntersectionObserver(
						(entries, observer) => {
							entries.forEach((entry) => {
								if (entry.isIntersecting && !this.isRendered) {
									this.isRendered = true;

									// Grab any custom icon markup BEFORE we wipe the element's
									// content below (this.innerHTML = ...), since the icon
									// <template> tags live alongside the video <template> and
									// would otherwise be lost.
									const playTemplate = this.querySelector("[data-icon-play]");
									const pauseTemplate = this.querySelector("[data-icon-pause]");
									const playIconHtml = playTemplate ? playTemplate.innerHTML : PLAY_ICON;
									const pauseIconHtml = pauseTemplate ? pauseTemplate.innerHTML : PAUSE_ICON;

									// Keep any other content that sits alongside the video
									// <template> (e.g. your .video__text-wrapper) - it would
									// otherwise be destroyed by the innerHTML replacement below.
									const overlayNodes = Array.from(this.children).filter(
										(node) => node !== this.videoTemplate && !node.matches("[data-icon-play], [data-icon-pause]")
									);

									const html = this.videoTemplate.innerHTML;
									this.innerHTML = html;
									this.classList.remove(LOADING_CLASS);
									this.video = this.querySelector("video");
									this.observeVideoPlayToggle();

									overlayNodes.forEach((node) => this.appendChild(node));

									if (this.hasControls) {
										this.renderControls(playIconHtml, pauseIconHtml);
									}

									this.video
										.play()
										.then(() => { })
										.catch(() => {
											document.addEventListener("click", this.powerSaverVideoPlay, {
												passive: true,
												bubbles: true,
												once: true,
											});
											document.addEventListener("touchstart", this.powerSaverVideoPlay, {
												passive: true,
												bubbles: true,
												once: true,
											});
										});

									observer.unobserve(entry.target);
								}
							});
						},
						{ root: null, rootMargin: "300px", threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] }
					);
					this.videoTemplateObserver.observe(this);
				}

				// Builds the play/pause button (bottom-right, per your CSS) and
				// wires up: click anywhere on the element toggles play/pause, and
				// the icon stays in sync with the video's real state (including
				// when observeVideoPlayToggle() auto-pauses it on scroll-out).
				renderControls(playIconHtml, pauseIconHtml) {
					this.classList.add("video-background--has-controls");

					this.toggleButton = document.createElement("button");
					this.toggleButton.type = "button";
					this.toggleButton.className = "video-background__toggle";
					this.toggleButton.setAttribute("aria-label", "Pause video");
					this.toggleButton.innerHTML =
						'<span class="video-background__icon" data-play-icon hidden>' +
						playIconHtml +
						"</span>" +
						'<span class="video-background__icon" data-pause-icon>' +
						pauseIconHtml +
						"</span>";

					this.appendChild(this.toggleButton);

					this.muteButton = document.createElement("button");
					this.muteButton.type = "button";
					this.muteButton.className = "video-background__toggle video-background__mute";
					this.muteButton.innerHTML =
						'<span class="video-background__icon" data-muted-icon>' +
						MUTED_ICON +
						"</span>" +
						'<span class="video-background__icon" data-unmuted-icon hidden>' +
						UNMUTED_ICON +
						"</span>";

					this.appendChild(this.muteButton);

					this.bindControls();
					this.syncControlsState();
					this.syncMuteState();
				}

				bindControls() {
					this.addEventListener("click", this.handleToggleClick);
					this.muteButton.addEventListener("click", this.handleMuteClick);
					this.video.addEventListener("play", this.syncControlsState);
					this.video.addEventListener("pause", this.syncControlsState);
					this.video.addEventListener("volumechange", this.syncMuteState);
				}

				handleToggleClick(event) {
					if (!this.video) return;

					// If the click landed on a real link (.video__text-wrapper
					// wraps an <a> when video_url is set) - let it navigate
					// normally, don't toggle play/pause.
					const link = event.target.closest("a");
					if (link && this.contains(link)) return;

					// Mute button handles its own click - don't toggle play/pause.
					if (event.target.closest(".video-background__mute")) return;

					event.preventDefault();

					if (this.video.paused) {
						this.video.play();
					} else {
						this.video.pause();
					}
				}

				syncControlsState() {
					if (!this.toggleButton || !this.video) return;

					const isPaused = this.video.paused;
					const playIcon = this.toggleButton.querySelector("[data-play-icon]");
					const pauseIcon = this.toggleButton.querySelector("[data-pause-icon]");

					if (playIcon) playIcon.hidden = !isPaused;
					if (pauseIcon) pauseIcon.hidden = isPaused;

					this.toggleButton.setAttribute("aria-label", isPaused ? "Play video" : "Pause video");
				}

				handleMuteClick(event) {
					if (!this.video) return;
					event.preventDefault();
					this.video.muted = !this.video.muted;
				}

				syncMuteState() {
					if (!this.muteButton || !this.video) return;

					const isMuted = this.video.muted;
					this.muteButton.querySelector("[data-muted-icon]").hidden = !isMuted;
					this.muteButton.querySelector("[data-unmuted-icon]").hidden = isMuted;
					this.muteButton.setAttribute("aria-label", isMuted ? "Unmute video" : "Mute video");
				}

				observeVideoPlayToggle() {
					if (!this.video) return;
					this.videoPlayObserver = new IntersectionObserver(
						(entries) => {
							entries.forEach((entry) => {
								const isIntersecting = entry.isIntersecting;
								if (isIntersecting && typeof this.video.play === "function") {
									const p = this.video.play();
									if (p !== undefined) p.then(() => { }).catch(() => { });
								}
								if (!isIntersecting && typeof this.video.pause === "function") {
									this.video.pause();
								}
							});
						},
						{ rootMargin: "0px", threshold: [0, 1] }
					);
					this.videoPlayObserver.observe(this.video);
				}

				disconnectedCallback() {
					if (this.videoTemplateObserver) this.videoTemplateObserver.disconnect();
					if (this.videoPlayObserver) this.videoPlayObserver.disconnect();
					if (this.hasControls) {
						this.removeEventListener("click", this.handleToggleClick);
						this.muteButton?.removeEventListener("click", this.handleMuteClick);
						this.video?.removeEventListener("play", this.syncControlsState);
						this.video?.removeEventListener("pause", this.syncControlsState);
						this.video?.removeEventListener("volumechange", this.syncMuteState);
					}
					if (this.videoId) {
						document.removeEventListener("click", this.powerSaverVideoPlay);
						document.removeEventListener("touchstart", this.powerSaverVideoPlay);
					}
				}
			}
		);
	}
}();